import { Booking } from '../booking';
import { getEventTypeStatusDisplay, getScheduleTypeDisplay } from '../event-type';
import { propExists } from '../../reporting-core/utils';
import { getPaymentMethodTypeDisplay } from '../payment';

export const COLUMN_NAME_BOOKING_ID = 'Booking ID';
export const COLUMN_NAME_CREATED_DATE = 'Created Date';
export const COLUMN_NAME_EVENT_ID = 'Event ID';
export const COLUMN_NAME_EVENT_NAME = 'Event Name';
export const COLUMN_NAME_EVENT_LOCATION = 'Event Location';
export const COLUMN_NAME_EVENT_START_DATE = 'Event Start Date';
export const COLUMN_NAME_EVENT_SCHEDULE_TYPE = 'Event Schedule Type';
export const COLUMN_NAME_EVENT_TYPE_STATUS = 'Event Type\'s Status';
export const COLUMN_NAME_NUMBER_OF_PARTICIPANTS = '# Of Participants';
export const COLUMN_NAME_PURCHASE_PRICE = 'Purchase Price (Tax-Inclusive)';
export const COLUMN_NAME_PURCHASE_TAX_AMOUNT = 'Purchase Tax Amount';
export const COLUMN_NAME_AMOUNT_PAID = 'Fees Paid';
export const COLUMN_NAME_AMOUNT_DUE = 'Fees Due';
export const COLUMN_NAME_AMOUNT_REFUNDED = 'Amount Refunded';
export const COLUMN_NAME_NUMBER_OF_VOUCHERS_USED = '# Of Vouchers Used';
export const COLUMN_NAME_VOUCHERS_AMOUNT_PAID = 'Amount Paid For Vouchers (Tax-Inclusive)';
export const COLUMN_NAME_INVOICE_ID = 'Invoice #';
export const COLUMN_NAME_BOOKING_CUSTOMER_ID = 'Booking Customer ID';
export const COLUMN_NAME_BOOKING_CUSTOMER_FIRST_NAME = 'Booking Customer First Name';
export const COLUMN_NAME_BOOKING_CUSTOMER_LAST_NAME = 'Booking Customer Last Name';
export const COLUMN_NAME_BOOKING_CUSTOMER_BIRTHDAY = 'Booking Customer Birthday';
export const COLUMN_NAME_BOOKING_CUSTOMER_EMAIL = 'Booking Customer Email';
export const COLUMN_NAME_PAYMENT_METHOD = 'Payment Method';
export const COLUMN_NAME_BOOKING_IS_CANCELLED = 'Booking Is Cancelled?';
export const COLUMN_NAME_PRICING_MATRIX_ID = 'Pricing Matrix ID';
export const COLUMN_NAME_PRICING_MATRIX_NAME = 'Pricing Matrix Name';
export const COLUMN_NAME_LINKED_PRIVATE_EVENT_ID = 'Linked Private Event ID';
export const COLUMN_NAME_LINKED_PRIVATE_EVENT_NAME = 'Linked Private Event Name';

export const COLUMN_NAMES: string[] = [
  COLUMN_NAME_BOOKING_ID,
  COLUMN_NAME_CREATED_DATE,
  COLUMN_NAME_EVENT_ID,
  COLUMN_NAME_EVENT_NAME,
  COLUMN_NAME_EVENT_LOCATION,
  COLUMN_NAME_EVENT_START_DATE,
  COLUMN_NAME_EVENT_SCHEDULE_TYPE,
  COLUMN_NAME_EVENT_TYPE_STATUS,
  COLUMN_NAME_NUMBER_OF_PARTICIPANTS,
  COLUMN_NAME_PURCHASE_PRICE,
  COLUMN_NAME_PURCHASE_TAX_AMOUNT,
  COLUMN_NAME_AMOUNT_PAID,
  COLUMN_NAME_AMOUNT_DUE,
  COLUMN_NAME_AMOUNT_REFUNDED,
  COLUMN_NAME_NUMBER_OF_VOUCHERS_USED,
  COLUMN_NAME_VOUCHERS_AMOUNT_PAID,
  COLUMN_NAME_INVOICE_ID,
  COLUMN_NAME_BOOKING_CUSTOMER_ID,
  COLUMN_NAME_BOOKING_CUSTOMER_FIRST_NAME,
  COLUMN_NAME_BOOKING_CUSTOMER_LAST_NAME,
  COLUMN_NAME_BOOKING_CUSTOMER_BIRTHDAY,
  COLUMN_NAME_BOOKING_CUSTOMER_EMAIL,
  COLUMN_NAME_PAYMENT_METHOD,
  COLUMN_NAME_BOOKING_IS_CANCELLED,
  COLUMN_NAME_PRICING_MATRIX_ID,
  COLUMN_NAME_PRICING_MATRIX_NAME,
  COLUMN_NAME_LINKED_PRIVATE_EVENT_ID,
  COLUMN_NAME_LINKED_PRIVATE_EVENT_NAME,
];

export const COLUMN_HELP_TEXT: {[key: string]: string} = {
  [COLUMN_NAME_LINKED_PRIVATE_EVENT_ID]: 'The ID of the private event that was created from this booking. Only applies if the booking was a private event request.',
  [COLUMN_NAME_LINKED_PRIVATE_EVENT_NAME]: 'The name of the private event that was created from this booking. Only applies if the booking was a private event request.',
}

export function getContentItem(booking: Booking,
                               selectedColumnName: string): string {
  switch (selectedColumnName) {
    case COLUMN_NAME_BOOKING_ID:
      return booking.id.toString();
    case COLUMN_NAME_CREATED_DATE:
      return booking.created_date;
    case COLUMN_NAME_EVENT_ID:
      return booking.event_id.toString();
    case COLUMN_NAME_EVENT_NAME:
      return booking.event_type_name;
    case COLUMN_NAME_EVENT_LOCATION:
      return booking.location_name;
    case COLUMN_NAME_EVENT_START_DATE:
      return booking.event_start_date;
    case COLUMN_NAME_EVENT_SCHEDULE_TYPE:
      return getScheduleTypeDisplay(booking.event_type_schedule_type);
    case COLUMN_NAME_EVENT_TYPE_STATUS:
      return getEventTypeStatusDisplay(booking.event_type_status);
    case COLUMN_NAME_NUMBER_OF_PARTICIPANTS:
      return booking.num_participants.toString();
    case COLUMN_NAME_PURCHASE_PRICE:
      return booking.invoice_amount.toString();
    case COLUMN_NAME_PURCHASE_TAX_AMOUNT:
      return booking.invoice_tax_amount.toString();
    case COLUMN_NAME_AMOUNT_PAID:
      return booking.invoice_amount_paid.toString();
    case COLUMN_NAME_AMOUNT_DUE:
      return booking.invoice_amount_due.toString();
    case COLUMN_NAME_AMOUNT_REFUNDED:
      return booking.invoice_refunded_amount.toString();
    case COLUMN_NAME_NUMBER_OF_VOUCHERS_USED:
      return booking.num_vouchers_used.toString();
    case COLUMN_NAME_VOUCHERS_AMOUNT_PAID:
      return booking.vouchers_amount_paid.toString();
    case COLUMN_NAME_INVOICE_ID:
      return booking.invoice_id.toString();
    case COLUMN_NAME_BOOKING_CUSTOMER_ID:
      if (!propExists(booking.booking_customer_id)) {
        return null;
      }
      return booking.booking_customer_id.toString();
    case COLUMN_NAME_BOOKING_CUSTOMER_FIRST_NAME:
      return booking.booking_customer_first_name;
    case COLUMN_NAME_BOOKING_CUSTOMER_LAST_NAME:
      return booking.booking_customer_last_name;
    case COLUMN_NAME_BOOKING_CUSTOMER_BIRTHDAY:
      return booking.booking_customer_birthday;
    case COLUMN_NAME_BOOKING_CUSTOMER_EMAIL:
      return booking.booking_customer_email;
    case COLUMN_NAME_PAYMENT_METHOD:
      return getPaymentMethodTypeDisplay(
        booking.invoice_payment_type,
        booking.invoice_payment_method_type
      );
    case COLUMN_NAME_BOOKING_IS_CANCELLED:
      return booking.is_cancelled ? 'True' : 'False';
    case COLUMN_NAME_PRICING_MATRIX_ID:
      if (!propExists(booking.pricing_matrix_id)) {
        return null;
      }
      return booking.pricing_matrix_id.toString();
    case COLUMN_NAME_PRICING_MATRIX_NAME:
      return booking.pricing_matrix_name;
    case COLUMN_NAME_LINKED_PRIVATE_EVENT_ID:
      if (!propExists(booking.linked_private_event_id)) {
        return null;
      }
      return booking.linked_private_event_id.toString();
    case COLUMN_NAME_LINKED_PRIVATE_EVENT_NAME:
      return booking.linked_private_event_name;
    default:
      return 'COLUMN NOT IMPLEMENTED';
  }
}
